import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import QuestionCard from "../../Components/Questionnaire/QuestionCard";
import { GOOGLE_SHEET_URL } from "../../../utils/googleSheetTwo";
import Swal from "sweetalert2";
interface Question {
  id: number;
  pertanyaan: string;
}

interface Summary {
  pertanyaan_id: number;
  rata_rata: number;
  total_responden: number;
}

const DEVICE_KEY = "kopdes_device_id";

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);

  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }

  return id;
};

const QuestionnairePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [summary, setSummary] = useState<Summary[]>([]);

  const [ratings, setRatings] = useState<Record<number, number>>({});
  const isComplete = questions.length > 0 && questions.every((q) => ratings[q.id] > 0);

  const deviceId = useMemo(() => getDeviceId(), []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [questionRes, summaryRes, myRatingRes] = await Promise.all([fetch(`${GOOGLE_SHEET_URL}?action=questions`), fetch(`${GOOGLE_SHEET_URL}?action=ratings`), fetch(`${GOOGLE_SHEET_URL}?action=my_rating&device_id=${deviceId}`)]);

      const questionJson = await questionRes.json();
      const summaryJson = await summaryRes.json();
      const myRatingJson = await myRatingRes.json();

      setQuestions(questionJson.data ?? []);
      setSummary(summaryJson.data ?? []);

      const temp: Record<number, number> = {};

      (myRatingJson.data ?? []).forEach((item: any) => {
        temp[item.pertanyaan_id] = item.rating;
      });

      setRatings(temp);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRating = (pertanyaanId: number, rating: number) => {
    setRatings((prev) => ({
      ...prev,
      [pertanyaanId]: rating,
    }));
  };

  const handleSubmit = async () => {
    if (!isComplete) return;

    try {
      setSaving(true);

      for (const item of questions) {
        const formData = new FormData();

        formData.append("device_id", deviceId);
        formData.append("pertanyaan_id", String(item.id));
        formData.append("rating", String(ratings[item.id]));

        await fetch(GOOGLE_SHEET_URL, {
          method: "POST",
          body: formData,
        });
      }

      await fetchData();

      await Swal.fire({
        icon: "success",
        title: "Penilaian Berhasil!",
        text: "Terima kasih telah memberikan penilaian untuk Program Koperasi Desa Merah Putih.",
        confirmButtonText: "Tutup",
        confirmButtonColor: "#65a30d",
        timer: 2500,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error(err);
      alert("Terjadi kesalahan.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Loading Overlay */}

      {/* {saving && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-2xl bg-white px-8 py-5 shadow-2xl">
            <Loader2 className="animate-spin text-lime-600" />

            <span className="font-medium">Menyimpan Penilaian...</span>
          </div>
        </div>
      )} */}

      <section className="relative overflow-hidden bg-gradient-to-b from-lime-50 via-white to-white py-10">
        {/* Blur */}

        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-lime-200/40 blur-[150px]" />

        <div className="absolute right-0 top-32 h-96 w-96 rounded-full bg-green-100/40 blur-[150px]" />

        <div className="relative mx-auto max-w-7xl px-5">
          {/* ===================== */}
          {/* Form Penilaian */}
          {/* ===================== */}

          <div className="mt-10">
            <div className="mb-10 text-center">
              <span className="inline-flex rounded-full bg-lime-100 px-5 py-2 text-sm font-semibold text-lime-700">⭐ Berikan Penilaian Anda</span>

              <h2 className="mt-5 text-4xl font-bold text-slate-800">Kuesioner Kepuasan Masyarakat</h2>

              <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-500">
                Anda dapat memberikan maupun mengubah penilaian kapan saja dari perangkat yang sama. Setiap perubahan akan langsung memperbarui hasil penilaian keseluruhan.
              </p>
            </div>

            {loading ? (
              <div className="grid gap-8 lg:grid-cols-2">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="h-72 animate-pulse rounded-3xl bg-gray-200" />
                ))}
              </div>
            ) : (
              <>
                <div className="grid gap-8 lg:grid-cols-2">
                  {questions.map((item) => (
                    <QuestionCard key={item.id} nomor={item.id} pertanyaan={item.pertanyaan} rating={ratings[item.id] ?? 0} onChange={(rating) => handleRating(item.id, rating)} />
                  ))}
                </div>

                <div className="mt-12 flex justify-center">
                  <button
                    onClick={handleSubmit}
                    disabled={!isComplete || saving}
                    className={`
      rounded-2xl
      px-10
      py-4
      text-lg
      font-semibold
      transition-all
      duration-300
      ${isComplete ? "bg-lime-600 text-white hover:bg-lime-700 shadow-lg shadow-lime-200" : "bg-gray-200 text-gray-400 cursor-not-allowed"}
    `}>
                    {saving ? "Menyimpan..." : "Kirim Penilaian"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ===================== */}
          {/* Footer */}
          {/* ===================== */}

          <div className="mt-24">
            <div
              className="
                rounded-[32px]
                border
                border-lime-100
                bg-gradient-to-r
                from-lime-600
                to-green-600
                px-10
                py-12
                text-center
                text-white
                shadow-2xl
              ">
              <h2 className="text-3xl font-bold">Terima Kasih 🙏</h2>

              <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-lime-100">
                Partisipasi Anda dalam memberikan penilaian sangat berarti untuk meningkatkan kualitas Program Koperasi Desa Merah Putih. Bersama kita membangun koperasi yang lebih transparan, bermanfaat, dan berkelanjutan.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default QuestionnairePage;
